import { useEffect, useState } from 'react'

import {
  Group,
  Select,
  Tooltip,
  Anchor,
  Text,
  Loader,
  TypographyStylesProvider,
} from '@mantine/core'
import DOMPurify from 'dompurify'

import { navigate } from '@redwoodjs/router'
import { useMutation, useQuery } from '@redwoodjs/web'

import Button from 'src/components/Buttons/Button/Button'
import Empty from 'src/components/Feedback/Empty/Empty'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { BULK_EMAIL } from 'src/graphql/email.graphql'
import {
  GET_EMAIL_TEMPLATES,
  RESOLVE_EMAIL_TEMPLATE,
} from 'src/graphql/emailTemplates.graphql'
import IconPlane from 'src/icons/IconPlane'

const getBulkEmailErrorMessage = (error) =>
  error?.graphQLErrors?.[0]?.extensions?.originalError?.message ||
  error?.graphQLErrors?.[0]?.message ||
  error?.message ||
  'Unable to send bulk email, please try again.'

const BulkEmailDrawer = ({ applicants, onClose, setLoading, setResults }) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState(null)

  //QUERIES:
  const { data: emailTemplatesList, loading: templatesLoading } = useQuery(
    GET_EMAIL_TEMPLATES,
    {
      variables: {
        pageInfo: {
          limit: 100,
          offset: 0,
        },
        orderBy: {
          field: 'title',
          direction: 'asc',
        },
      },
    }
  )

  const emailTemplates = emailTemplatesList?.emailTemplates?.items || []
  const previewApplicant = applicants[0]
  const { data: resolvedTemplatePreview, loading: previewLoading } = useQuery(
    RESOLVE_EMAIL_TEMPLATE,
    {
      variables: {
        emailTemplateId: selectedTemplateId,
        applicantId: previewApplicant?.applicantId,
      },
      skip: !selectedTemplateId || !previewApplicant?.applicantId,
    }
  )

  // MUTATIONS:
  const [bulkEmail, { loading: bulkEmailLoading }] = useMutation(BULK_EMAIL, {
    onCompleted: (data) => {
      const results = data?.bulkEmail?.applicants
      if (setResults) {
        setResults(
          results?.length
            ? results.map((result) => ({
                applicantId: result.applicantId,
                success: result.success,
                errorMsg: result.errorMsg,
              }))
            : applicants.map((a) => ({
                applicantId: a.applicantId,
                success: true,
              }))
        )
      }
      toast('Emails queued successfully', 'success')
    },
    onError: (error) => {
      const errorMessage = getBulkEmailErrorMessage(error)
      if (setResults) {
        setResults(
          applicants.map((a) => ({
            applicantId: a.applicantId,
            success: false,
            errorMsg: errorMessage,
          }))
        )
      }
      toast(errorMessage, 'error')
    },
  })

  useEffect(() => {
    if (setLoading) {
      setLoading(bulkEmailLoading)
    }
  }, [bulkEmailLoading, setLoading])

  const handleSubmit = () => {
    bulkEmail({
      variables: {
        applicants: applicants.map((applicant) => applicant.applicantId),
        emailTemplateId: selectedTemplateId,
      },
    })
  }

  const templateOptions = emailTemplates.map((template) => ({
    value: template.emailTemplateId,
    label: template.title,
  }))

  const selectedTemplate = emailTemplates.find(
    (template) => template.emailTemplateId === selectedTemplateId
  )
  const previewTemplate =
    resolvedTemplatePreview?.resolveEmailTemplate || selectedTemplate
  const previewAttachments = previewTemplate?.attachments || []

  return (
    <>
      <div className="flex flex-col gap-4">
        {templatesLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader size="sm" color="gray" />
          </div>
        ) : emailTemplates.length > 0 ? (
          <>
            <div className="flex flex-row items-center justify-between">
              <Text size="xs">Template</Text>
              <div className="w-[250px]">
                <Select
                  placeholder="Select a template"
                  data={templateOptions}
                  value={selectedTemplateId}
                  onChange={setSelectedTemplateId}
                  searchable
                  clearable
                  nothingFoundMessage="No templates found"
                  checkIconPosition="right"
                  size="xs"
                  classNames={{
                    input: 'text-xs text-right',
                    dropdown: 'border-doubleNickel-gray-200',
                    option:
                      'text-xs text-doubleNickel-gray-900 data-[combobox-selected]:bg-transparent data-[combobox-active]:bg-transparent hover:bg-doubleNickel-gray-100',
                  }}
                />
              </div>
            </div>
            {selectedTemplate && (
              <div className="flex flex-col gap-2">
                <Text size="xs">Message Preview</Text>
                <div className="flex flex-col gap-2 rounded-md border border-gray-200 p-3 text-xs text-gray-700">
                  {previewLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader size="xs" color="gray" />
                    </div>
                  ) : (
                    <>
                      <div className="font-semibold">
                        {previewTemplate?.subject}
                      </div>
                      <TypographyStylesProvider className="text-xs">
                        <div
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(
                              previewTemplate?.body || ''
                            ),
                          }}
                        />
                      </TypographyStylesProvider>
                      {previewAttachments.length > 0 && (
                        <div className="flex flex-col gap-1 border-t border-gray-200 pt-2">
                          <Text size="xs" fw={500}>
                            Attachments
                          </Text>
                          {previewAttachments.map((attachment) => (
                            <div
                              key={attachment.emailAttachmentId}
                              className="rounded-sm border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-700"
                            >
                              {attachment.fileName}
                            </div>
                          ))}
                        </div>
                      )}
                      {previewApplicant && applicants.length > 1 && (
                        <Text size="xs" className="text-gray-500">
                          Previewing personalized content for{' '}
                          {previewApplicant.name}. Each applicant receives their
                          own version.
                        </Text>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center gap-6">
            <Empty
              title="No templates found"
              subtitle="Templates are required for bulk messages"
            />
            <Anchor
              onClick={() => navigate('/settings?tab=email')}
              className="mt-auto"
            >
              Add a template in settings
            </Anchor>
          </div>
        )}
      </div>

      <Group className="mt-auto grid grid-cols-2">
        <Button
          text="Cancel"
          variant="outline"
          onClick={onClose}
          disabled={bulkEmailLoading}
        />
        <Tooltip
          disabled={selectedTemplateId && applicants.length > 0}
          label="Please select a template and at least one applicant to send the message"
        >
          <Button
            text={'Send'}
            variant="filled"
            lefticon={<IconPlane />}
            disabled={!selectedTemplateId || applicants.length === 0}
            loading={bulkEmailLoading}
            onClick={() => {
              handleSubmit()
            }}
          />
        </Tooltip>
      </Group>
    </>
  )
}

export default BulkEmailDrawer
